import splitLines from 'split-lines';

import { type Text, TextRange } from '@gitbook/slate';
import { getTokenStyles } from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { calculateLineOffsets, lookupLineForOffset } from './lines';

export const TOKEN_MARK = 'code-syntax-token';

/**
 * Return a decoration range for the given text.
 */
export function createDecoration({
    line,
    lineStart,
    lineEnd,
    start,
    end,
    classes,
    markType,
}): TextRange | null {
    if (start >= lineEnd || end <= lineStart) {
        // Ignore, the token is not in the text
        return null;
    }

    if (!classes) {
        // Ignore, the token is not decorated
        return null;
    }

    // Shrink to this text boundaries
    start = Math.max(start, lineStart);
    end = Math.min(end, lineEnd);

    // Now shift offsets to be relative to this text
    start -= lineStart;
    end -= lineStart;

    if (start === end) {
        return null;
    }

    return TextRange.create({
        anchorKey: line.text.key,
        anchorOffset: start,
        focusKey: line.text.key,
        focusOffset: end,
        marks: [{ type: markType, data: { classes } }],
    });
}

/**
 * Transform code syntax highlighting tokens into Slate decorations.
 */
export function transformTokensToDecorations({
    lines,
    tokens,
}: {
    lines: Array<{ raw: string; text: Text }>;
    tokens: Array<any>;
}) {
    const lineOffsets = calculateLineOffsets(lines);

    let cursor = 0;

    function processToken(
        token: string | { type: string; token: any; children?: any[] },
        classes = ''
    ) {
        if (typeof token === 'string') {
            cursor = processTokenContent({
                content: token,
                cursor,
                classes,
                decorations,
                lineOffsets,
                lines,
            });
        } else if (token.type !== 'shiki') {
            token.children?.forEach((token) => processToken(token, classes));
        } else {
            // Compute the classes for this token
            classes += getTokenStyles(token.token);
            classes = classes.trim();

            if (typeof token.token.content === 'string') {
                cursor = processTokenContent({
                    content: token.token.content,
                    cursor,
                    classes,
                    decorations,
                    lineOffsets,
                    lines,
                });
            } else {
                // Nested tokens
                if (Array.isArray(token.token.content)) {
                    token.token.content.forEach((nested) => processToken(nested, classes));
                } else {
                    processToken(token.token.content, classes);
                }
            }
        }
    }

    const decorations: Array<TextRange> = [];
    tokens.forEach((token) => processToken(token));
    return decorations;
}

/**
 * Process decorations from a single line of content from a token
 */
function processContentLine(props: {
    contentLine: string;
    contentLineOffset: number;
    classes: string;
    decorations: Array<TextRange>;
    lineOffsets: Map<number, [number, number]>;
    lines: Array<{ raw: string; text: Text }>;
}): number {
    const { contentLine, contentLineOffset, classes, lineOffsets, lines, decorations } = props;

    const lineIndex = lookupLineForOffset(contentLineOffset, lineOffsets);
    const line = lines[lineIndex];
    const lineOffset = lineOffsets.get(lineIndex);
    assert(lineOffset, 'Expect there to always be a line offset to match a line');

    const [lineStart, lineEnd] = lineOffset;
    const start = contentLineOffset;
    const end = contentLineOffset + contentLine.length;

    const decoration = createDecoration({
        line,
        lineStart,
        lineEnd,
        start,
        end,
        classes,
        markType: TOKEN_MARK,
    });

    if (decoration) {
        decorations.push(decoration);
    }

    return end;
}

/**
 * Process the content of a token
 */
function processTokenContent(props: {
    content: string;
    cursor: number;
    classes: string;
    decorations: Array<TextRange>;
    lineOffsets: Map<number, [number, number]>;
    lines: Array<{ raw: string; text: Text }>;
}): number {
    const { content, cursor, classes, decorations, lineOffsets, lines } = props;
    if (content.length === 0) {
        return cursor;
    }
    // Split token into lines and process each line
    const contentLines = splitLines(content);
    const contentLineOffset = cursor;
    contentLines.forEach((contentLine) =>
        processContentLine({
            contentLine,
            contentLineOffset,
            classes,
            decorations,
            lineOffsets,
            lines,
        })
    );
    // return the new cursor position
    return cursor + content.length;
}
